import { User } from '@auth0/auth0-react'
import { backOfficeReducer, BackOfficeState, createBackOfficeSaga } from '@om1/back-office-module'
import { cohortReducer, CohortState, createCohortsSaga } from '@om1/cohort-module'
import { createFalconApiSaga, falconApiReducer, FalconApiState } from '@om1/falcon-api'
import { createInsightsLibrarySaga, insightsLibraryReducer, InsightsLibraryState } from '@om1/insights-library-module'
import {
    createExplorerAssistantConversationsSaga,
    explorerAssistantConversationReducer,
    ExplorerAssistantConversationState
} from '@om1/platform-assistant-ui'
import { platformConfigReducer, PlatformConfigState } from '@om1/platform-config/state'
import { createNotificationSaga, notificationActions, notificationReducer, NotificationState } from '@om1/platform-notifications'
import { createPlatformSettingsSaga, platformSettingsReducer, PlatformSettingsState } from '@om1/platform-settings-module'
import { createTrackingSaga } from '@om1/platform-tracking'
import { configureStore } from '@reduxjs/toolkit'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { connectRouter, RouterState } from 'connected-react-router'
import { LocationState } from 'history'
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack'
import { AnyAction, applyMiddleware, CombinedState, combineReducers, Middleware, Reducer } from 'redux'
import dynamicMiddlewares from 'redux-dynamic-middlewares'
import { SagaMiddleware } from 'redux-saga'
import { all, put, spawn } from 'redux-saga/effects'
import keepAlive from './keep-alive'
import { languageReducer } from './shared/providers/internationalization/language-reducer'
import { LanguageState } from './shared/providers/internationalization/language-state'

export const createReducer = (history: any) =>
    combineReducers({
        router: connectRouter(history),
        language: languageReducer,
        notification: notificationReducer,
        // modules:
        backOffice: backOfficeReducer,
        cohort: cohortReducer,
        platformConfig: platformConfigReducer,
        falconApi: falconApiReducer,
        platformSettings: platformSettingsReducer,
        insightsLibrary: insightsLibraryReducer,
        explorerAssistantConversation: explorerAssistantConversationReducer
    })
export const appReducer = createReducer(window.history)

export type PlatformState = ReturnType<typeof appReducer>

export function createAppStore(
    reducer: Reducer<
        CombinedState<{
            router: RouterState<LocationState>
            language: LanguageState
            notification: NotificationState
            // modules:
            backOffice: BackOfficeState
            cohort: CohortState
            platformConfig: PlatformConfigState
            falconApi: FalconApiState
            platformSettings: PlatformSettingsState
            insightsLibrary: InsightsLibraryState
            explorerAssistantConversation: ExplorerAssistantConversationState
        }>,
        AnyAction
    >,
    sagaMiddleware: SagaMiddleware,
    ...additionalMiddlewares: Middleware[]
) {
    const middlewares = [dynamicMiddlewares, ...additionalMiddlewares]
    return configureStore({
        reducer: reducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: { ignoredActions: ['@@notifications/success', '@@notifications/error', '@@notifications/warning'] }
            }).concat(sagaMiddleware),
        enhancers: [applyMiddleware(...middlewares)]
    })
}

export type AppStore = ReturnType<typeof createAppStore>

function createOnFail(sagaName: string) {
    function* onFail(error: any) {
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        const errorMessage = `${sagaName} module has produced too many uncaught errors and is shutting down, these exceptions should be handled.`
        yield console.warn(errorMessage)
        yield console.error(error)
        yield put(notificationActions.error(errorMessage))
    }
    function* onEachError(_next: any, error: any, _saga: any) {
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        const errorMessage = `${sagaName} module has produced an error and is restarting, these exceptions should be handled.`
        yield console.warn(errorMessage)
        yield console.error(error)
        yield put(notificationActions.warning(errorMessage))
    }
    return { onFail, onEachError, disableWarnings: true }
}

export function createRootSaga(
    enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
    analytics?: AnalyticsBrowser,
    user?: User
) {
    const notificationSaga = createNotificationSaga(enqueueSnackbar)
    // modules:
    const falconApiSaga = createFalconApiSaga()
    const backOfficeSaga = createBackOfficeSaga()
    const cohortsSaga = createCohortsSaga()
    const platformSettingsSaga = createPlatformSettingsSaga()
    const insightsLibrarySaga = createInsightsLibrarySaga()
    const explorerAssistantConversationsSaga = createExplorerAssistantConversationsSaga()

    let sagas = [
        { saga: notificationSaga, name: 'Notifications' },
        // modules:
        { saga: backOfficeSaga, name: 'BackOffice' },
        { saga: cohortsSaga, name: 'Cohorts' },
        { saga: falconApiSaga, name: 'FalconAPI' },
        { saga: platformSettingsSaga, name: 'PlatformSettings' },
        { saga: insightsLibrarySaga, name: 'InsightsLibrary' },
        { saga: explorerAssistantConversationsSaga, name: 'ExplorerAssistantConversations' }
    ]

    if (analytics) {
        const trackingSaga = createTrackingSaga(analytics, user)
        sagas.push({ saga: trackingSaga, name: 'User Tracking' })
    }

    return function* () {
        yield all(sagas.map((saga) => spawn(keepAlive(saga.saga, createOnFail(saga.name)))))
    }
}
